import { useState, useEffect, useCallback, useRef } from 'react';
import { joinQueue, leaveQueue, getQueueStatus, getQueueStats } from '../api/quickDuel';
import {
    initWebSocket,
    disconnectWebSocket as disconnectWebSocketApi,
    isWebSocketConnected,
    subscribeToQuickDuelQueue,
    subscribeToQuickDuelStats
} from '../api/websocket';
import { getAccessToken } from '../api/token';

/**
 * React хук для управления очередью быстрых дуэлей
 * @param {Object} options - Опции хука
 * @param {boolean} options.autoConnect - Автоматически подключать WebSocket
 * @param {Function} options.onMatchFound - Колбэк при нахождении матча
 * @param {Function} options.onError - Колбэк при ошибке
 * @returns {Object} Методы и состояния для работы с очередью
 */
export function useQuickDuelQueue(options = {}) {
    const {
        autoConnect = true,
        onMatchFound,
        onError,
    } = options;

    // Состояния
    const [isInQueue, setIsInQueue] = useState(false);
    const [queueStatus, setQueueStatus] = useState(null);
    const [queueStats, setQueueStats] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Данные очереди
    const [format, setFormat] = useState(null);
    const [roundType, setRoundType] = useState(null);
    const [estimatedWaitSeconds, setEstimatedWaitSeconds] = useState(null);
    const [positionInQueue, setPositionInQueue] = useState(null);

    const queueSubscriptionRef = useRef(null);
    const statsSubscriptionRef = useRef(null);
    const isMountedRef = useRef(false);

    // Подключение к WebSocket
    const connectWebSocket = useCallback(async () => {
        try {
            const token = getAccessToken();
            if (!token) {
                throw new Error('JWT токен не найден');
            }

            initWebSocket(
                (data) => {
                    if (!isMountedRef.current) return;

                    if (data.error) {
                        console.error('[useQuickDuelQueue] WebSocket error:', data.message);
                        setError(data.message);
                        onError?.(data.message);
                    } else if (data.connected) {
                        console.log('[useQuickDuelQueue] WebSocket подключён');
                    }
                },
                null,
                token
            );

            // Подписка на события очереди
            queueSubscriptionRef.current = subscribeToQuickDuelQueue((event) => {
                console.log('[useQuickDuelQueue] Получено событие очереди:', event);

                switch (event.type) {
                    case 'QUEUE_STATUS_CHANGED':
                        setQueueStatus(event.status);
                        setFormat(event.preferredFormat);
                        setEstimatedWaitSeconds(event.estimatedWaitSeconds ?? null);
                        setPositionInQueue(event.positionInQueue ?? null);
                        setIsInQueue(event.status === 'WAITING');
                        break;

                    case 'MATCH_FOUND':
                        setIsInQueue(false);
                        setQueueStatus('MATCHED');
                        onMatchFound?.(event);
                        break;

                    case 'TEAM_MATCH_FOUND':
                        setIsInQueue(false);
                        setQueueStatus('MATCHED');
                        onMatchFound?.(event);
                        break;

                    case 'QUEUE_LEFT':
                        setIsInQueue(false);
                        setQueueStatus('CANCELLED');
                        break;

                    case 'QUEUE_ERROR':
                        setError(event.message);
                        onError?.(event.message);
                        break;

                    case 'QUEUE_POSITION_UPDATED':
                        setPositionInQueue(event.position);
                        break;

                    default:
                        console.log('[useQuickDuelQueue] Неизвестный тип события:', event.type);
                }
            });

            // Подписка на статистику
            statsSubscriptionRef.current = subscribeToQuickDuelStats((event) => {
                setQueueStats({
                    activeQueues: event.activeQueues,
                    soloQueues: event.soloQueues,
                    team2v2Queues: event.team2v2Queues,
                    averageWaitSeconds: event.averageWaitSeconds,
                    yourPosition: null,
                });
            });

            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'WebSocket connection failed';
            setError(errorMessage);
            onError?.(errorMessage);
        }
    }, [onMatchFound, onError]);

    // Отключение от WebSocket
    const disconnectWebSocket = useCallback(() => {
        if (queueSubscriptionRef.current) {
            queueSubscriptionRef.current.unsubscribe();
            queueSubscriptionRef.current = null;
        }
        if (statsSubscriptionRef.current) {
            statsSubscriptionRef.current.unsubscribe();
            statsSubscriptionRef.current = null;
        }
        disconnectWebSocketApi();
    }, []);

    // Войти в очередь
    const joinQueueHandler = useCallback(async (
        selectedFormat,
        selectedRoundType,
        additionalData = {}
    ) => {
        setIsLoading(true);
        setError(null);

        try {
            const request = {
                preferredFormat: selectedFormat,
                preferredRoundType: selectedRoundType,
                ...additionalData,
            };

            const response = await joinQueue(request);
            
            setQueueStatus(response.status);
            setFormat(response.preferredFormat);
            setRoundType(response.preferredRoundType);
            setEstimatedWaitSeconds(response.estimatedWaitSeconds);
            setIsInQueue(true);

            // Если WebSocket не подключён, подключаем
            if (!isWebSocketConnected() && autoConnect) {
                await connectWebSocket();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to join queue';
            setError(errorMessage);
            onError?.(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [autoConnect, connectWebSocket, onError]);

    // Выйти из очереди
    const leaveQueueHandler = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            await leaveQueue();
            setIsInQueue(false);
            setQueueStatus('CANCELLED');
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to leave queue';
            setError(errorMessage);
            onError?.(errorMessage);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [onError]);

    // Обновить статус
    const refreshStatus = useCallback(async () => {
        try {
            const status = await getQueueStatus();
            setQueueStatus(status.status);
            setFormat(status.preferredFormat);
            setRoundType(status.preferredRoundType);
            setEstimatedWaitSeconds(status.estimatedWaitSeconds);
            setPositionInQueue(null); // Позиция обновляется через WebSocket
            setIsInQueue(status.status === 'WAITING');
        } catch (err) {
            if (err.response?.status === 404) {
                // Нет активной заявки
                setIsInQueue(false);
                setQueueStatus(null);
                return;
            }
            setError(err.message || 'Failed to refresh status');
        }
    }, []);

    // Проверка статуса при монтировании
    useEffect(() => {
        isMountedRef.current = true;
        refreshStatus();
        
        return () => {
            isMountedRef.current = false;
            disconnectWebSocket();
        };
    }, [disconnectWebSocket, refreshStatus]);

    return {
        // Состояния
        isInQueue,
        queueStatus,
        queueStats,
        isLoading,
        error,
        
        // Данные очереди
        format,
        roundType,
        estimatedWaitSeconds,
        positionInQueue,
        
        // Действия
        joinQueue: joinQueueHandler,
        leaveQueue: leaveQueueHandler,
        refreshStatus,
        connectWebSocket,
        disconnectWebSocket,
    };
}

export default useQuickDuelQueue;
